<script>
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import deLocale from '@fullcalendar/core/locales/de';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import map from 'lodash/map';
import LeaveModel from '@models/Leave';
import { DateTime } from 'luxon';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';

export default {
  components: {
    FullCalendar,
    FontAwesomeIcon,
  },
  props: {
    search: {
      type: String,
      default: null
    },
    areasAndLocations: {
      type: Array,
      default: null
    }
  },  
  data() {
    return {
      loading: false,
      selectedEvent: null,
      calendarOptions: {
        plugins: [dayGridPlugin, timeGridPlugin],
        initialView: 'dayGridMonth',
        locales: [deLocale],
        locale: this.$i18n.locale,
        firstDay: 1,
        height: 'auto',
        dayMaxEvents: true,
        headerToolbar: {
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek',
        },
        events: this.fetchEvents,
        eventClick: this.openEvent,
        eventDidMount: this.setEventTitle,
      },
      filters: {
          show_admins: 1,
          show_drivers: 1,
      }
    };
  },
  watch: {
    '$i18n.locale'(locale) {
      this.$refs.calendar.getApi().setOption('locale', locale);
    },
    filters: {
      handler(newFilterVal) {
        this.$nextTick(() => {
          this.$refs.calendar.getApi().refetchEvents();
        });
      },
      immediate: false,
      deep: true,
    },
    areasAndLocations: {
      handler() {
        this.$nextTick(() => {
          this.$refs.calendar.getApi().refetchEvents();
        });
      },
      immediate: false,
      deep: true,
    },
  },
  methods: {
    fetchEvents(fetchInfo, successCallback, failureCallback) {
      this.loading = true;

      this.$http
        .get('admin/leaves/calendar', {
          params: {
            start: fetchInfo.startStr.substring(0, 10),
            end: fetchInfo.endStr.substring(0, 10),
            search: this.search,
            show_admins: this.filters.show_admins,
            show_drivers: this.filters.show_drivers,
            areas_and_locations: map(this.areasAndLocations, 'id'),
          },
        })
        .then((response) => {
          const leaves = Array.isArray(response.data) ? response.data : response.data.data || [];
          successCallback(leaves.map(this.toCalendarEvent));
        })
        .catch(failureCallback)
        .finally(() => {
          this.loading = false;
        });
    },
    toCalendarEvent(leave) {
      return {
        id: String(leave.id),
        title: leave.title || '-',
        start: leave.start,
        end: leave.end,
        allDay: true,
        display: 'block',
        backgroundColor: leave.confirmed ? '#28a745' : '#ffc107',
        borderColor: leave.confirmed ? '#28a745' : '#ffc107',
        textColor: leave.confirmed ? '#fff' : '#212529',
        extendedProps: {
          confirmed: leave.confirmed,
          amountOfDays: leave.amount_of_days,
          description: leave.description,
        },
      };
    },
    setEventTitle(info) {
      const status = info.event.extendedProps.confirmed
        ? this.$t('messages.confirmed')
        : this.$t('messages.pending');
      const details = [
        info.event.title,
        status,
        `${this.$t('table_labels.amount_of_days')}: ${info.event.extendedProps.amountOfDays}`,
        info.event.extendedProps.description,
      ];
      info.el.title = details.filter(Boolean).join('\n');
    },
    openEvent(info) {
      this.selectedEvent = {
        title: info.event.title,
        start: info.event.start,
        end: info.event.end,
        confirmed: info.event.extendedProps.confirmed,
        amount_of_days: info.event.extendedProps.amountOfDays,
        description: info.event.extendedProps.description,
        id: info.event.id,
      };
      this.$refs.eventDetails.show();
    },
    refetchEvents() {
      this.$refs.calendar.getApi().refetchEvents();
    },
    exportPdf(id) {
      this.$wait.start(`downloading${id}`);
      this.$http
        .get(`leaves/export/${id}`, { responseType: 'blob' })
        .then((response) => {
          this.$wait.end(`downloading${id}`);
          const headers = response.headers;
          const blob = new Blob([response.data], { type: headers['content-type'] });
          let filename = `Leave#${id}`;
          const match = response.headers['content-disposition'].match(/filename="([^"]+)"/);

          if (match && match.length > 1) {
            filename = match[1];
          }

          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        })
        .catch(() => this.$wait.end(`downloading${id}`));
    },
    formatEventRange(event) {
      const start = DateTime.fromJSDate(event.start).setLocale(this.$i18n.locale);
      
      if (!event.end) {
        return `${start.toFormat('dd.LL.yyyy')} - ${start.toFormat('dd.LL.yyyy')}`;
      }

      let end = DateTime.fromJSDate(event.end).setLocale(this.$i18n.locale);
  
      // Only subtract a day if FullCalendar shifted the end date ahead
      if (end > start) {
        end = end.minus({ days: 1 });
      }
      
      return `${start.toFormat('dd.LL.yyyy')} - ${end.toFormat('dd.LL.yyyy')}`;
    },
    confirm(id) {
      if (confirm(this.$t('messages.confirm_leave'))) {
        this.$wait.start(`confirm ${id}`);
        new LeaveModel({ id: `confirm/${id}` })
          .save()
          .then(() => {
            this.$wait.end(`confirm ${id}`);
          })
          .catch(() => {
            this.$wait.end(`confirm ${id}`);
          });
      }
    },
    closeModal() {
      this.$refs.eventDetails.hide();
      this.selectedEvent = null;
      this.refetchEvents();
    },
    async deleteEntity(id) {
      if (confirm(this.$t('alerts.delete_entity', { entity: this.$t('entities.leave') }))) {
        this.$wait.start('leave processing');
        const leave = new LeaveModel({ id });
        await leave.delete();
        this.$wait.end('leave processing');
        this.closeModal();
      }
    },
  },
};
</script>

<template>
  <div class="position-relative border bg-white p-2 p-md-3">
    <b-overlay :show="loading" rounded="sm">
      <div class="d-flex flex-wrap mb-3">
        <b-badge variant="warning" class="mr-2 px-2 py-2">{{ $t('messages.pending') }}</b-badge>
        <b-badge variant="success" class="mr-2 px-2 py-2">{{ $t('messages.confirmed') }}</b-badge>
        <b-form-checkbox v-model="filters.show_admins" name="check-button" switch :value="1" :unchecked-value="0" class="mr-2" @click.stop>
          <span>{{ $t('fields.admin') }}</span>
        </b-form-checkbox>
        <b-form-checkbox v-model="filters.show_drivers" name="check-button" switch :value="1" :unchecked-value="0" @click.stop>
          <span>{{ $t('fields.driver') }}</span>
        </b-form-checkbox>
      </div>
      <div class="calendar-wrapper">
        <FullCalendar ref="calendar" :options="calendarOptions" />
      </div>
      <b-modal ref="eventDetails" :title="$t('titles.leave')" hide-header @hidden="closeModal">
        <b-table-simple v-if="selectedEvent" borderless small>
            <b-tbody>
            <b-tr>
                <b-th>{{ $t('fields.name') }}</b-th>
                <b-td>{{ selectedEvent.title || '-' }}</b-td>
            </b-tr>
            <b-tr>
                <b-th>{{ $t('fields.start_date') }} / {{ $t('fields.end_date') }}</b-th>
                <b-td>{{ formatEventRange(selectedEvent) }}</b-td>
            </b-tr>
            <b-tr>
                <b-th>{{ $t('fields.amount_of_days') }}</b-th>
                <b-td>{{ selectedEvent.amount_of_days || '-' }}</b-td>
            </b-tr>
            </b-tbody>
        </b-table-simple>
        <template slot="modal-footer" slot-scope="{ close }">
          <div class="w-100 d-flex justify-content-between align-items-center">
            <!-- LEFT SIDE -->
            <b-btn variant="outline-secondary" @click="close()">
              {{ $t('actions.ok') }}
            </b-btn>

            <!-- RIGHT SIDE -->
            <div v-if="selectedEvent" class="d-flex">
                <b-btn
                    :disabled="selectedEvent.confirmed == true"
                    class="mr-2"
                    :variant="selectedEvent.confirmed == true ? `outline-success` : `success`"
                    @click="confirm(selectedEvent.id)"
                >
                    <FontAwesomeIcon :icon="['fas', 'check']" />
                </b-btn>
                <b-btn class="mr-2" variant="outline-primary" :disabled="$wait.is(`downloading${selectedEvent.id}`)" @click="exportPdf(selectedEvent.id)">
                    <FontAwesomeIcon :icon="['fas', 'file-export']" />
                    <FontAwesomeIcon v-if="$wait.is(`downloading${selectedEvent.id}`)" class="ml-1" icon="spinner" spin />
                </b-btn>
                <b-btn variant="outline-danger" @click.stop="deleteEntity(selectedEvent.id)">
                    <FontAwesomeIcon :icon="['fas', 'times-circle']" />
                </b-btn>
            </div>
          </div>
        </template>
       </b-modal>      
    </b-overlay>
  </div>
</template>
<style>
.calendar-wrapper {
  --fc-button-text-color: #ffffff;
  --fc-button-bg-color: #636363;
  --fc-button-border-color: #636363;
  --fc-button-hover-bg-color: #636363;
  --fc-button-hover-border-color: #636363;
  
  /* Active Button Variables (Native FullCalendar vars) */
  --fc-button-active-bg-color: #191919;
  --fc-button-active-border-color: #191919;

  /* Custom Variables for Links and Navigation (Our own vars) */
  --cal-link-color: #191919;
  --cal-link-hover-color: #636363;
  --cal-nav-btn-color: #191919;
  --cal-nav-btn-hover-color: #636363;
}

.calendar-wrapper .fc a.fc-col-header-cell-cushion,
.calendar-wrapper .fc a.fc-daygrid-day-number,
.calendar-wrapper .fc a.fc-timegrid-slot-label-cushion,
.calendar-wrapper .fc a.fc-timegrid-axis-cushion,
.calendar-wrapper .fc .fc-daygrid-more-link {
  color: var(--cal-link-color) !important;
  text-decoration: none !important;
}

.calendar-wrapper .fc a.fc-col-header-cell-cushion:hover,
.calendar-wrapper .fc a.fc-daygrid-day-number:hover,
.calendar-wrapper .fc .fc-daygrid-more-link:hover {
  color: var(--cal-link-hover-color) !important;
  text-decoration: underline !important;
}

.calendar-wrapper .fc .fc-button {
  border-radius: 0 !important;
  text-transform: capitalize !important;
  padding: 0.2em 0.45em !important;
  font-size: 14px !important;
}

.calendar-wrapper .fc .fc-prev-button,
.calendar-wrapper .fc .fc-next-button,
.calendar-wrapper .fc .fc-today-button {
  background-color: var(--cal-nav-btn-color) !important;
  border-color: var(--cal-nav-btn-color) !important;
}

.calendar-wrapper .fc .fc-prev-button:hover,
.calendar-wrapper .fc .fc-next-button:hover,
.calendar-wrapper .fc .fc-today-button:hover {
  background-color: var(--cal-nav-btn-hover-color) !important;
  border-color: var(--cal-nav-btn-hover-color) !important;
}

.calendar-wrapper .fc .fc-event {
  cursor: pointer !important;
}

.calendar-wrapper .fc-toolbar-title {
  font-size: clamp(1rem, 1vw + 0.5rem, 1.2rem) !important; 
}
</style>